<script setup lang="ts">
import { computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import {
  ServiceCompanyAuditUsersApi,
  type ServiceCompanyAuditUser,
  type ServiceCompanyAuditUserForm,
} from '@/api/service-company-audit-users';

const { t } = useI18n();

interface Props {
  serviceCompanyId: number;
  auditUser?: ServiceCompanyAuditUser | null;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const queryClient = useQueryClient();

const isEditing = !!props.auditUser;
const title = computed(() => (isEditing ? 'Editar Usuario de Auditoría' : 'Nuevo Usuario de Auditoría'));

const validationSchema = yup.object({
  email: yup
    .string()
    .required('El email es requerido')
    .email('Debe ser un email válido'),
});

const { values, errors, defineField, handleSubmit, meta } = useForm<ServiceCompanyAuditUserForm>({
  validationSchema,
  initialValues: {
    email: props.auditUser?.email || '',
  },
});

const [email, emailAttrs] = defineField('email');

const createMutation = useMutation({
  mutationFn: () => ServiceCompanyAuditUsersApi.create(props.serviceCompanyId, values),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['serviceCompanyAuditUsers', props.serviceCompanyId] });
    emit('success');
    emit('close');
  },
});

const updateMutation = useMutation({
  mutationFn: () => ServiceCompanyAuditUsersApi.update(props.auditUser!.id, values),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['serviceCompanyAuditUsers', props.serviceCompanyId] });
    emit('success');
    emit('close');
  },
});

const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

const onSubmit = handleSubmit(() => {
  if (isEditing && props.auditUser) {
    updateMutation.mutate();
  } else {
    createMutation.mutate();
  }
});

function handleClose() {
  emit('close');
}
</script>

<template>
  <v-card>
    <v-card-title class="mt-2 d-flex font-weight-bold">
      {{ title }}
    </v-card-title>
    <v-card-text>
      <form @submit.prevent="onSubmit">
        <v-text-field
          v-model="email"
          label="Email"
          type="email"
          :error-messages="errors.email"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="emailAttrs"
        />
      </form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="isLoading"
        @click="handleClose"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!meta.valid"
        :loading="isLoading"
        @click="onSubmit"
      >
        {{ isEditing ? 'Actualizar' : 'Crear' }}
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="createMutation.isError.value || updateMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      {{ t('userSession.defaultError') }} No se puede repetir el email.
    </v-alert>
  </v-card>
</template>
